<script lang="ts">
  import type { Component } from "svelte";
  import Modal from "@shared/ui/Modal.svelte";
  import LawyerSelect from "@shared/ui/LawyerSelect.svelte";
  import LawyerMultiSelect from "@shared/ui/LawyerMultiSelect.svelte";
  import FirmSelect from "@shared/ui/FirmSelect.svelte";
  import AccountMultiSelect from "@shared/ui/AccountMultiSelect.svelte";
  import {
    validateContractCreate,
    type ValidationIssue,
  } from "@domain/contract/validation";
  import {
    createContract,
    patchContract,
  } from "@entities/contract/store.svelte";
  import type { Contract } from "@domain/contract/model";
  import type {
    ContractCreateLineItemRequest,
    ContractCreateRequest,
    ContractLineItem,
    ContractPatchRequest,
    ContractPipeline,
    LpCountType,
  } from "@shared/types/contract";
  import {
    CONTRACT_PIPELINE_LABELS,
    LP_COUNT_TYPE_LABELS,
  } from "@shared/types/contract";
  import type { Opportunity } from "@domain/opportunity/model";
  import { mergeRelatedLawyerIds } from "./related-lawyers";

  interface Props {
    open: boolean;
    fixedFirmId?: string;
    opportunities?: Opportunity[];
    initialOpportunityId?: string;
    /** 関心弁護士の初期値。 弁護士詳細の通常「契約を追加」で現在の弁護士を渡す。
     * 営業アクション起点の作成では渡さない (対象弁護士を関心弁護士へ自動解釈しない)。
     * 保存前に変更可能。 */
    initialMainInterestLawyerId?: string;
    /** 営業アクションの participant_lawyer_ids 由来の関連弁護士初期値。 商談を
     * 選ぶとその related_lawyer_ids と union + 重複排除される。 保存前に編集可能。 */
    initialRelatedLawyerIds?: string[];
    mode?: "create" | "edit";
    initial?: Contract | null;
    LineItemsEditor?: Component<{
      contractId?: string | null;
      canEdit?: boolean;
      autoload?: boolean;
      items?: ContractLineItem[];
    }> | null;
    lineItemsCanEdit?: boolean;
    onClose: () => void;
    onCreated?: (contract: Contract) => void;
    onUpdated?: (contract: Contract) => void;
  }

  let {
    open,
    fixedFirmId,
    opportunities = [],
    initialOpportunityId,
    initialMainInterestLawyerId,
    initialRelatedLawyerIds = [],
    mode = "create",
    initial = null,
    LineItemsEditor = null,
    lineItemsCanEdit = true,
    onClose,
    onCreated,
    onUpdated,
  }: Props = $props();

  let firmId = $state(fixedFirmId ?? "");
  let opportunityId = $state("");

  let dealName = $state("");
  let pipeline = $state<"" | ContractPipeline>("");
  let dealStage = $state("");
  let forecastCategory = $state("");
  let assigneeAccountIds = $state<string[]>([]);
  let agencyName = $state("");
  let lpCountType = $state<"" | LpCountType>("");

  let isIndividualContract = $state(false);
  let mainInterestLawyerId = $state("");
  let relatedLawyerIds = $state<string[]>([]);
  let individualContractMemo = $state("");

  let monthlyAmount = $state("");
  let mrrManual = $state("");
  let initialAmount = $state("");
  let spotAmount = $state("");
  let annualContractAmount = $state("");
  let proposedAcv = $state("");
  let proposedMrr = $state("");
  let activeUserCount = $state("");

  let signedOn = $state("");
  let startsOn = $state("");
  let endsOn = $state("");
  let autoRenewal = $state(true);
  let cancellationNoticePeriodDays = $state("");
  let mrrRecognitionStartMonth = $state("");
  let mrrRecognitionEndMonth = $state("");

  let billingIrregular = $state(false);
  let proBillingMemo = $state("");
  let proBillingMethod = $state("");
  let proBillingEmail = $state("");
  let proBillingPostalCode = $state("");
  let proBillingAddressLine = $state("");
  let proBillingBuilding = $state("");
  let proPaymentDue = $state("");
  let memo = $state("");
  let createLineItems = $state<ContractLineItem[]>([]);

  let submitting = $state(false);
  let issues = $state<ValidationIssue[]>([]);
  let serverError = $state<string | null>(null);

  const isEdit = $derived(mode === "edit");
  const modalTitle = $derived(isEdit ? "契約を編集" : "契約を追加");
  const submitLabel = $derived(isEdit ? "保存" : "作成");
  const submittingLabel = $derived(isEdit ? "保存中…" : "作成中…");

  $effect(() => {
    if (fixedFirmId !== undefined && !isEdit) firmId = fixedFirmId;
  });

  let lastOpenSeen = $state(false);
  $effect(() => {
    if (open && !lastOpenSeen) {
      if (isEdit && initial) {
        applyInitial(initial);
      } else {
        reset();
        if (initialOpportunityId) opportunityId = initialOpportunityId;
        if (initialMainInterestLawyerId)
          mainInterestLawyerId = initialMainInterestLawyerId;
        relatedLawyerIds = [...initialRelatedLawyerIds];
      }
    }
    lastOpenSeen = open;
  });

  const selectedOpportunity = $derived(
    opportunityId
      ? (opportunities.find((o) => o.id === opportunityId) ?? null)
      : null,
  );

  /// 関連商談セレクトの変更ハンドラ。 商談選択は明示アクションなので、選んだ商談の
  /// related_lawyer_ids を現在の関連弁護士へ union + 重複排除で足し込む (商談の
  /// related が空配列なら「追加なし」で現状維持。 解除 (指定なしへ戻す) は related を
  /// 減らさない)。 結果はフォームの関連弁護士欄で確認・再編集できる。
  function selectOpportunity(nextOpportunityId: string) {
    opportunityId = nextOpportunityId;
    const opportunity = nextOpportunityId
      ? (opportunities.find((o) => o.id === nextOpportunityId) ?? null)
      : null;
    relatedLawyerIds = mergeRelatedLawyerIds(
      relatedLawyerIds,
      opportunity?.related_lawyer_ids ?? [],
    );
  }

  const contractMonths = $derived(monthCount(startsOn, endsOn));

  function applyInitial(contract: Contract) {
    firmId = fixedFirmId ?? contract.firm_id;
    opportunityId = contract.opportunity_id ?? "";
    dealName = contract.deal_name ?? "";
    pipeline = contract.pipeline ?? "";
    dealStage = contract.deal_stage ?? "";
    forecastCategory = contract.forecast_category ?? "";
    assigneeAccountIds = contract.assignee_account_ids ?? [];
    agencyName = contract.agency_name ?? "";
    lpCountType = contract.lp_count_type ?? "";
    isIndividualContract = contract.is_individual_contract;
    mainInterestLawyerId = contract.main_interest_lawyer_id ?? "";
    relatedLawyerIds = [...contract.related_lawyer_ids];
    individualContractMemo = contract.individual_contract_memo ?? "";
    monthlyAmount = contract.monthly_amount ?? "";
    mrrManual = contract.mrr_manual ?? "";
    initialAmount = contract.initial_amount ?? "";
    spotAmount = contract.spot_amount ?? "";
    annualContractAmount = contract.annual_contract_amount ?? "";
    proposedAcv = contract.proposed_acv ?? "";
    proposedMrr = contract.proposed_mrr ?? "";
    activeUserCount = contract.active_user_count?.toString() ?? "";
    signedOn = contract.signed_on ?? "";
    startsOn = contract.starts_on ?? "";
    endsOn = contract.ends_on ?? "";
    autoRenewal = contract.auto_renewal;
    cancellationNoticePeriodDays =
      contract.cancellation_notice_period_days?.toString() ?? "";
    mrrRecognitionStartMonth = dateToMonthInput(
      contract.mrr_recognition_start_month,
    );
    mrrRecognitionEndMonth = dateToMonthInput(
      contract.mrr_recognition_end_month,
    );
    billingIrregular = contract.billing_irregular;
    proBillingMemo = contract.pro_billing_memo ?? "";
    proBillingMethod = contract.pro_billing_method ?? "";
    proBillingEmail = contract.pro_billing_email ?? "";
    proBillingPostalCode = contract.pro_billing_postal_code ?? "";
    proBillingAddressLine = contract.pro_billing_address_line ?? "";
    proBillingBuilding = contract.pro_billing_building ?? "";
    proPaymentDue = contract.pro_payment_due ?? "";
    memo = contract.memo ?? "";
    createLineItems = [];
    issues = [];
    serverError = null;
  }

  function reset() {
    firmId = fixedFirmId ?? "";
    opportunityId = "";
    dealName = "";
    pipeline = "";
    dealStage = "";
    forecastCategory = "";
    assigneeAccountIds = [];
    agencyName = "";
    lpCountType = "";
    isIndividualContract = false;
    mainInterestLawyerId = "";
    relatedLawyerIds = [];
    individualContractMemo = "";
    monthlyAmount = "";
    mrrManual = "";
    initialAmount = "";
    spotAmount = "";
    annualContractAmount = "";
    proposedAcv = "";
    proposedMrr = "";
    activeUserCount = "";
    signedOn = "";
    startsOn = "";
    endsOn = "";
    autoRenewal = true;
    cancellationNoticePeriodDays = "";
    mrrRecognitionStartMonth = "";
    mrrRecognitionEndMonth = "";
    billingIrregular = false;
    proBillingMemo = "";
    proBillingMethod = "";
    proBillingEmail = "";
    proBillingPostalCode = "";
    proBillingAddressLine = "";
    proBillingBuilding = "";
    proPaymentDue = "";
    memo = "";
    createLineItems = [];
    issues = [];
    serverError = null;
  }

  function close() {
    reset();
    onClose();
  }

  function issueFor(field: keyof ContractCreateRequest): string | undefined {
    return issues.find((i) => i.field === field)?.message;
  }

  function blankToNull(v: string): string | null {
    const t = v.trim();
    return t.length > 0 ? t : null;
  }

  function blankToNullInt(v: string): number | null {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) && Number.isInteger(n) ? n : null;
  }

  function dateToMonthInput(value: string | null): string {
    return value ? value.slice(0, 7) : "";
  }

  function monthToDate(value: string): string | null {
    const t = value.trim();
    return t.length > 0 ? `${t}-01` : null;
  }

  function monthCount(start: string, end: string): string {
    if (!start || !end) return "—";
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s)
      return "—";
    return String(
      (e.getFullYear() - s.getFullYear()) * 12 +
        e.getMonth() -
        s.getMonth() +
        1,
    );
  }

  function buildCreatePayload(): ContractCreateRequest {
    return {
      firm_id: firmId,
      opportunity_id: opportunityId === "" ? null : opportunityId,
      deal_name: blankToNull(dealName),
      pipeline: pipeline === "" ? null : pipeline,
      deal_stage: blankToNull(dealStage),
      forecast_category: blankToNull(forecastCategory),
      agency_name: blankToNull(agencyName),
      assignee_account_ids: assigneeAccountIds,
      lp_count_type: lpCountType === "" ? null : lpCountType,
      is_individual_contract: isIndividualContract,
      primary_lawyer_id:
        selectedOpportunity?.primary_lawyer_id ??
        initial?.primary_lawyer_id ??
        null,
      related_lawyer_ids: relatedLawyerIds,
      main_interest_lawyer_id:
        blankToNull(mainInterestLawyerId) ??
        selectedOpportunity?.main_interest_lawyer_id ??
        initial?.main_interest_lawyer_id ??
        null,
      individual_contract_memo: blankToNull(individualContractMemo),
      monthly_amount: blankToNull(monthlyAmount),
      mrr_manual: blankToNull(mrrManual),
      initial_amount: blankToNull(initialAmount),
      annual_contract_amount: blankToNull(annualContractAmount),
      proposed_acv: blankToNull(proposedAcv),
      proposed_mrr: blankToNull(proposedMrr),
      spot_amount: blankToNull(spotAmount),
      active_user_count: blankToNullInt(activeUserCount),
      signed_on: blankToNull(signedOn),
      starts_on: blankToNull(startsOn),
      ends_on: blankToNull(endsOn),
      auto_renewal: autoRenewal,
      cancellation_notice_period_days: blankToNullInt(
        cancellationNoticePeriodDays,
      ),
      mrr_recognition_start_month: monthToDate(mrrRecognitionStartMonth),
      mrr_recognition_end_month: monthToDate(mrrRecognitionEndMonth),
      billing_irregular: billingIrregular,
      pro_billing_memo: blankToNull(proBillingMemo),
      pro_billing_method: blankToNull(proBillingMethod),
      pro_billing_email: blankToNull(proBillingEmail),
      pro_billing_postal_code: blankToNull(proBillingPostalCode),
      pro_billing_address_line: blankToNull(proBillingAddressLine),
      pro_billing_building: blankToNull(proBillingBuilding),
      pro_payment_due: blankToNull(proPaymentDue),
      memo: blankToNull(memo),
      line_items: isEdit ? undefined : buildLineItemsPayload(),
    };
  }

  function buildLineItemsPayload(): ContractCreateLineItemRequest[] {
    return createLineItems.map((item, index) => ({
      product_id: item.product_id,
      name: item.name.trim(),
      module_type: item.module_type,
      revenue_type: item.revenue_type,
      acceptance_status: item.acceptance_status,
      schedule_start_on: item.schedule_start_on,
      schedule_end_on: item.schedule_end_on,
      recognition_month: item.recognition_month,
      proposed_amount_ex_tax: item.proposed_amount_ex_tax,
      split_count: item.split_count,
      sort_order: item.sort_order ?? index,
      schedules: item.schedules.map((schedule) => ({
        schedule_date: schedule.schedule_date,
        revenue: schedule.revenue,
        billing_amount: schedule.billing_amount,
      })),
    }));
  }

  function buildPatchPayload(
    payload: ContractCreateRequest,
  ): ContractPatchRequest {
    const patch: Partial<ContractCreateRequest> = { ...payload };
    delete patch.firm_id;
    delete patch.line_items;
    return patch as ContractPatchRequest;
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;

    const payload = buildCreatePayload();
    issues = validateContractCreate(payload);
    if (issues.length > 0) return;

    submitting = true;
    serverError = null;
    try {
      if (isEdit) {
        if (!initial) throw new Error("編集対象の契約が見つかりません");
        const contract = await patchContract(
          initial.id,
          buildPatchPayload(payload),
        );
        onUpdated?.(contract);
      } else {
        const contract = await createContract(payload);
        onCreated?.(contract);
      }
      reset();
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title={modalTitle} onClose={close}>
  <form id="contract-create-form" onsubmit={handleSubmit}>
    <fieldset>
      <legend>基本情報</legend>
      {#if !fixedFirmId}
        <label>
          <span>事務所 <em>*</em></span>
          <FirmSelect
            bind:value={firmId}
            optional={false}
            required
            placeholder="選択してください"
          />
          {#if issueFor("firm_id")}<small class="error"
              >{issueFor("firm_id")}</small
            >{/if}
        </label>
      {/if}
      {#if opportunities.length > 0}
        <label>
          <span>関連商談</span>
          <select
            value={opportunityId}
            onchange={(ev) => selectOpportunity(ev.currentTarget.value)}
          >
            <option value="">(指定なし)</option>
            {#each opportunities as o (o.id)}
              <option value={o.id}>{o.name}</option>
            {/each}
          </select>
        </label>
      {/if}
      <label>
        <span>取引名 <em>*</em></span>
        <input bind:value={dealName} />
        {#if issueFor("deal_name")}<small class="error"
            >{issueFor("deal_name")}</small
          >{/if}
      </label>
      <div class="grid-2">
        <label>
          <span>パイプライン</span>
          <select bind:value={pipeline}>
            <option value="">(未設定)</option>
            {#each Object.entries(CONTRACT_PIPELINE_LABELS) as [value, label] (value)}
              <option {value}>{label}</option>
            {/each}
          </select>
        </label>
        <label>
          <span>取引ステージ</span>
          <input bind:value={dealStage} />
        </label>
        <label>
          <span>売上予測分類</span>
          <input bind:value={forecastCategory} />
        </label>
        <label>
          <span>担当者</span>
          <AccountMultiSelect bind:value={assigneeAccountIds} />
        </label>
      </div>
    </fieldset>

    <fieldset>
      <legend>区分</legend>
      <div class="grid-2">
        <label>
          <span>代理店</span>
          <input bind:value={agencyName} />
        </label>
        <label>
          <span>LPカウント</span>
          <select bind:value={lpCountType}>
            <option value="">(未設定)</option>
            {#each Object.entries(LP_COUNT_TYPE_LABELS) as [value, label] (value)}
              <option {value}>{label}</option>
            {/each}
          </select>
        </label>
      </div>
    </fieldset>

    <details class="fieldset-like">
      <summary>関係者（社内）</summary>
      <label class="checkbox">
        <input type="checkbox" bind:checked={isIndividualContract} />
        <span>個人契約</span>
      </label>
      <label>
        <span>関心弁護士</span>
        <LawyerSelect
          bind:value={mainInterestLawyerId}
          firmId={firmId || undefined}
          placeholder="関心弁護士を選択"
        />
      </label>
      <label>
        <span>関連弁護士</span>
        <LawyerMultiSelect
          bind:value={relatedLawyerIds}
          firmId={firmId || undefined}
        />
      </label>
      <label>
        <span>個人契約メモ</span>
        <textarea bind:value={individualContractMemo} rows="2"></textarea>
      </label>
    </details>

    <fieldset>
      <legend>金額</legend>
      <div class="grid-3">
        <label>
          <span>月額</span>
          <input
            bind:value={monthlyAmount}
            inputmode="decimal"
            placeholder="50000"
          />
          {#if issueFor("monthly_amount")}<small class="error"
              >{issueFor("monthly_amount")}</small
            >{/if}
        </label>
        <label>
          <span>MRR手動入力</span>
          <input bind:value={mrrManual} inputmode="decimal" />
          {#if issueFor("mrr_manual")}<small class="error"
              >{issueFor("mrr_manual")}</small
            >{/if}
        </label>
        <label>
          <span>初期費用</span>
          <input
            bind:value={initialAmount}
            inputmode="decimal"
            placeholder="100000"
          />
          {#if issueFor("initial_amount")}<small class="error"
              >{issueFor("initial_amount")}</small
            >{/if}
        </label>
        <label>
          <span>スポット費用</span>
          <input bind:value={spotAmount} inputmode="decimal" />
          {#if issueFor("spot_amount")}<small class="error"
              >{issueFor("spot_amount")}</small
            >{/if}
        </label>
        <label>
          <span>年契約金額</span>
          <input
            bind:value={annualContractAmount}
            inputmode="decimal"
            placeholder="600000"
          />
          {#if issueFor("annual_contract_amount")}<small class="error"
              >{issueFor("annual_contract_amount")}</small
            >{/if}
        </label>
        <label>
          <span>提案金額(ACV)</span>
          <input bind:value={proposedAcv} inputmode="decimal" />
          {#if issueFor("proposed_acv")}<small class="error"
              >{issueFor("proposed_acv")}</small
            >{/if}
        </label>
        <label>
          <span>提案金額(MRR)</span>
          <input bind:value={proposedMrr} inputmode="decimal" />
          {#if issueFor("proposed_mrr")}<small class="error"
              >{issueFor("proposed_mrr")}</small
            >{/if}
        </label>
        <label>
          <span>アカウント数</span>
          <input
            bind:value={activeUserCount}
            inputmode="numeric"
            placeholder="5"
          />
        </label>
      </div>
    </fieldset>

    {#if LineItemsEditor}
      <fieldset>
        <legend>商談商品</legend>
        <LineItemsEditor
          contractId={isEdit ? (initial?.id ?? null) : null}
          canEdit={lineItemsCanEdit}
          autoload={isEdit}
          bind:items={createLineItems}
        />
      </fieldset>
    {/if}

    <fieldset>
      <legend>日付・契約条件</legend>
      <div class="grid-3">
        <label>
          <span>契約開始日</span>
          <input type="date" bind:value={startsOn} />
        </label>
        <label>
          <span>契約期間終了日</span>
          <input type="date" bind:value={endsOn} />
        </label>
        <label>
          <span>契約月数</span>
          <input value={contractMonths} readonly />
        </label>
        <label>
          <span>締結日</span>
          <input type="date" bind:value={signedOn} />
        </label>
        <label>
          <span>解約予告(日数)</span>
          <input
            bind:value={cancellationNoticePeriodDays}
            inputmode="numeric"
            placeholder="30"
          />
        </label>
        <label>
          <span>MRR計上開始月</span>
          <input type="month" bind:value={mrrRecognitionStartMonth} />
        </label>
        <label>
          <span>MRR計上終了月</span>
          <input type="month" bind:value={mrrRecognitionEndMonth} />
        </label>
      </div>
      <label class="checkbox">
        <input type="checkbox" bind:checked={autoRenewal} />
        <span>自動更新</span>
      </label>
    </fieldset>

    <fieldset>
      <legend>請求情報（Pro）</legend>
      <label class="checkbox">
        <input type="checkbox" bind:checked={billingIrregular} />
        <span>請求書発行イレギュラー</span>
      </label>
      <div class="grid-2">
        <label>
          <span>Pro請求書発行方法</span>
          <input bind:value={proBillingMethod} />
        </label>
        <label>
          <span>Pro請求先メール</span>
          <input type="email" bind:value={proBillingEmail} />
        </label>
        <label>
          <span>Pro支払期限</span>
          <input bind:value={proPaymentDue} />
        </label>
        <label>
          <span>郵便番号</span>
          <input bind:value={proBillingPostalCode} />
        </label>
        <label>
          <span>住所</span>
          <input bind:value={proBillingAddressLine} />
        </label>
        <label>
          <span>建物名</span>
          <input bind:value={proBillingBuilding} />
        </label>
      </div>
      <label>
        <span>Pro請求メモ</span>
        <textarea bind:value={proBillingMemo} rows="2"></textarea>
      </label>
      <label>
        <span>メモ</span>
        <textarea bind:value={memo} rows="3"></textarea>
      </label>
    </fieldset>

    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}
      >キャンセル</button
    >
    <button
      type="submit"
      form="contract-create-form"
      class="primary"
      disabled={submitting}
    >
      {submitting ? submittingLabel : submitLabel}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  fieldset,
  .fieldset-like {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  legend,
  summary {
    padding: 0 6px;
    font-size: 0.75rem;
    color: #4b5563;
    font-weight: 600;
  }
  summary {
    cursor: pointer;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  label.checkbox {
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  input,
  select,
  textarea {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  input[readonly] {
    background: #f9fafb;
    color: #6b7280;
  }
  textarea {
    resize: vertical;
  }
  .grid-2,
  .grid-3 {
    display: grid;
    gap: 10px;
  }
  .grid-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .grid-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .error {
    color: #b91c1c;
    font-size: 0.75rem;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  @media (max-width: 760px) {
    .grid-2,
    .grid-3 {
      grid-template-columns: 1fr;
    }
  }
</style>
